import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { favoritesAPI } from "@/api/favorites";
import { FavoriteEntityType, FavoriteIdsByType } from "@/types/favorites";

// Types for favorites state
type FavoritesByType = {
  device: Set<number>;
  organization: Set<number>;
  dashboard: Set<number>;
  geozone: Set<number>;
  device_usage_profile: Set<number>;
};

type InitializedState = {
  device: boolean;
  organization: boolean;
  dashboard: boolean;
  geozone: boolean;
  device_usage_profile: boolean;
};

interface FavoritesContextValue {
  favoritesByType: FavoritesByType;
  loading: boolean;
  initialized: InitializedState;
  loadFavoriteIds: (entityType: FavoriteEntityType) => Promise<void>;
  isFavorite: (entityType: FavoriteEntityType, entityId: number | string) => boolean;
  toggleFavorite: (entityType: FavoriteEntityType, entityId: number | string) => Promise<boolean>;
  reloadAllFavorites: () => Promise<void>;
}

const FavoritesContext = createContext<FavoritesContextValue | undefined>(undefined);

export const useFavorites = (): FavoritesContextValue => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error("useFavorites must be used within a FavoritesProvider");
  }
  return context;
};

interface FavoritesProviderProps {
  children: ReactNode;
}

export const FavoritesProvider = ({ children }: FavoritesProviderProps): JSX.Element => {
  const [favoritesByType, setFavoritesByType] = useState<FavoritesByType>({
    device: new Set(),
    organization: new Set(),
    dashboard: new Set(),
    geozone: new Set(),
    device_usage_profile: new Set(),
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [initialized, setInitialized] = useState<InitializedState>({
    device: false,
    organization: false,
    dashboard: false,
    geozone: false,
    device_usage_profile: false,
  });
  // Apply a bulk /rest/favorites/ids response to state (all types at once).
  const applyBulkResponse = useCallback((data: FavoriteIdsByType): void => {
    setFavoritesByType({
      device: new Set(data.device ?? []),
      organization: new Set(data.organization ?? []),
      dashboard: new Set(data.dashboard ?? []),
      geozone: new Set(data.geozone ?? []),
      device_usage_profile: new Set(data.device_usage_profile ?? []),
    });
    setInitialized({
      device: true,
      organization: true,
      dashboard: true,
      geozone: true,
      device_usage_profile: true,
    });
  }, []);

  // Load favorite IDs for a specific entity type (only the type the page needs).
  const loadFavoriteIds = useCallback(
    async (entityType: FavoriteEntityType): Promise<void> => {
      if (initialized[entityType]) return;

      try {
        setLoading(true);
        const response = await favoritesAPI.getFavoriteEntityIds(entityType);
        const favoriteIds = new Set(response.data[entityType] ?? []);

        setFavoritesByType((prev) => ({
          ...prev,
          [entityType]: favoriteIds,
        }));

        setInitialized((prev) => ({
          ...prev,
          [entityType]: true,
        }));
      } catch (error) {
        console.error(`Failed to load ${entityType} favorites:`, error);
      } finally {
        setLoading(false);
      }
    },
    [initialized]
  );

  // Check if an entity is favorited
  const isFavorite = useCallback(
    (entityType: FavoriteEntityType, entityId: number | string): boolean => {
      return favoritesByType[entityType].has(Number(entityId));
    },
    [favoritesByType]
  );

  // Toggle favorite status
  const toggleFavorite = useCallback(
    async (entityType: FavoriteEntityType, entityId: number | string): Promise<boolean> => {
      const numericId = Number(entityId);
      const wasIsFavorite = favoritesByType[entityType].has(numericId);

      // Optimistic update
      setFavoritesByType((prev) => {
        const newSet = new Set(prev[entityType]);
        if (wasIsFavorite) {
          newSet.delete(numericId);
        } else {
          newSet.add(numericId);
        }
        return {
          ...prev,
          [entityType]: newSet,
        };
      });

      try {
        const response = await favoritesAPI.toggle(entityType, entityId);
        const isNowFavorite = response.data as unknown as boolean;

        // Update with server response
        setFavoritesByType((prev) => {
          const newSet = new Set(prev[entityType]);
          if (isNowFavorite) {
            newSet.add(numericId);
          } else {
            newSet.delete(numericId);
          }
          return {
            ...prev,
            [entityType]: newSet,
          };
        });

        return isNowFavorite;
      } catch (error) {
        // Revert optimistic update on error
        setFavoritesByType((prev) => {
          const newSet = new Set(prev[entityType]);
          if (wasIsFavorite) {
            newSet.add(numericId);
          } else {
            newSet.delete(numericId);
          }
          return {
            ...prev,
            [entityType]: newSet,
          };
        });

        console.error("Failed to toggle favorite:", error);
        throw error;
      }
    },
    [favoritesByType]
  );

  // Reload all favorites (forces refresh from server for all entity types) in one call
  const reloadAllFavorites = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await favoritesAPI.getFavoriteEntityIds();
      applyBulkResponse(response.data);
    } catch (error) {
      console.error("Failed to reload favorites:", error);
    } finally {
      setLoading(false);
    }
  }, [applyBulkResponse]);

  const value: FavoritesContextValue = {
    favoritesByType,
    loading,
    initialized,
    loadFavoriteIds,
    isFavorite,
    toggleFavorite,
    reloadAllFavorites,
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};
